'use client'
import { AnimatePresence, easeOut, motion } from "framer-motion"
import { useTranslations } from "next-intl"
import { useEffect, useRef, useState } from "react"

type Props = {
  onPress: () => void
  onTimeout: () => void
  title?: string
  description?: string
}

export default function PaymentIncompleteToast({
  onPress,
  onTimeout,
  title,
  description,
}: Props) {
  const t = useTranslations()
  const toastTitle = title ?? t("pricing.paymentIncompleteTitle")
  const toastDesc = description ?? t("pricing.paymentIncompleteDesc", { plan: t("pricing.premium") })
  const [visible, setVisible] = useState(true)
  const closedByPress = useRef(false)

  useEffect(() => {
    const id = window.setTimeout(() => setVisible(false), 5000)
    return () => window.clearTimeout(id)
  }, [])

  return (
    <AnimatePresence
      onExitComplete={() => {
        if (closedByPress.current) onPress()
        else onTimeout()
      }}
    >
      {visible && (
        <motion.div
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          transition={{ duration: 0.28, ease: easeOut }}
          className="fixed bottom-[max(16px,env(safe-area-inset-bottom))] right-4 z-[100] w-[min(calc(100%-32px),440px)]"
        >
          <button
            type="button"
            className="flex h-[88px] w-full items-center gap-3 rounded-2xl bg-white px-4 text-left shadow-[0px_2px_8px_0px_rgba(0,0,0,0.1)]"
            onClick={() => {
              closedByPress.current = true
              setVisible(false)
            }}
          >
            <div className="flex size-14 shrink-0 items-center justify-center overflow-clip rounded-xl bg-[#eaf0f8]">
              <img
                src="/icons/Luxury.svg"
                alt=""
                aria-hidden
                className="size-14 object-contain"
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[16px] font-semibold leading-5 text-black">
                {toastTitle}
              </p>
              <p className="mt-2 line-clamp-2 text-[12px] leading-4 text-black/70">
                {toastDesc}
              </p>
            </div>
            <img
              src="/imgs/payment-toast-hand.png"
              alt=""
              aria-hidden
              className="size-9 shrink-0 object-contain opacity-70"
            />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
